/**
 * Valnexo CRM - Module Detail Page
 * =================================
 * Page de consultation d'un module avec:
 * - Liste des contenus (vidéos, docs, articles)
 * - Lecteur de contenu
 * - Quiz en fin de module
 * - Suivi de progression
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { academyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
    ArrowLeft, Play, FileText, Video, BookOpen, CheckCircle2,
    Clock, Award, ChevronRight, ChevronLeft, Send, RefreshCw,
    Zap, HardHat, Monitor, Eye, Check, X, AlertCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import ModuleResources from '@/components/academy/ModuleResources';

// Icônes par type de contenu
const CONTENT_ICONS = {
    video: Video,
    document: FileText,
    article: BookOpen
};

// Composant lecteur de contenu
const ContentViewer = ({ content, onComplete }) => {
    const timeSpentRef = useRef(0);
    const timerRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        // Reset le chrono quand le contenu change
        timeSpentRef.current = 0;
        
        // Démarrer le chrono
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            timeSpentRef.current += 1;
            // Sauvegarder silencieusement toutes les 30 secondes
            if (timeSpentRef.current % 30 === 0) {
                onComplete(content?.id, timeSpentRef.current, false);
            }
        }, 1000);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [content?.id]);

    const handleMarkComplete = () => {
        onComplete(content.id, timeSpentRef.current, true);
        toast.success('Contenu marqué comme vu');
    };

    if (!content) {
        return (
            <div className="flex items-center justify-center h-96">
                <p className="text-muted-foreground">Sélectionnez un contenu</p>
            </div>
        );
    }

    // Construire l'URL embed pour YouTube de manière fiable
    const getYouTubeEmbedUrl = (url) => {
        let videoId = '';
        try {
            if (url.includes('youtu.be/')) {
                videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
            } else if (url.includes('youtube.com/watch')) {
                const urlObj = new URL(url);
                videoId = urlObj.searchParams.get('v');
            } else if (url.includes('youtube.com/embed/')) {
                videoId = url.split('embed/')[1]?.split(/[?&#]/)[0];
            }
        } catch {}
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
        }
        return url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
    };

    const getVimeoEmbedUrl = (url) => {
        let videoId = '';
        try {
            const match = url.match(/vimeo\.com\/(\d+)/);
            if (match) videoId = match[1];
        } catch {}
        if (videoId) {
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url.replace('vimeo.com/', 'player.vimeo.com/video/');
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">{content.title}</h2>
                    {content.description && (
                        <p className="text-muted-foreground text-sm mt-1">{content.description}</p>
                    )}
                </div>
                <Button onClick={handleMarkComplete} variant="outline" size="sm" data-testid="mark-complete-btn">
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Marquer comme vu
                </Button>
            </div>

            <Separator />

            {/* Affichage selon le type */}
            {content.type === 'video' && content.url && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden" data-testid="video-container">
                    {content.url.includes('youtube') || content.url.includes('youtu.be') ? (
                        <iframe
                            key={`yt-${content.id}`}
                            className="w-full h-full"
                            src={getYouTubeEmbedUrl(content.url)}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                            title={content.title}
                            referrerPolicy="strict-origin-when-cross-origin"
                            sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-popups-to-escape-sandbox"
                        />
                    ) : content.url.includes('vimeo') ? (
                        <iframe
                            key={`vm-${content.id}`}
                            className="w-full h-full"
                            src={getVimeoEmbedUrl(content.url)}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={content.title}
                        />
                    ) : (
                        <video 
                            key={`vid-${content.id}`}
                            ref={videoRef}
                            className="w-full h-full" 
                            controls 
                            src={content.url}
                            playsInline
                        />
                    )}
                </div>
            )}

            {content.type === 'document' && content.url && (
                <div className="space-y-4">
                    <div className="p-6 bg-slate-50 rounded-lg border text-center">
                        <FileText className="h-16 w-16 mx-auto text-slate-400 mb-4" />
                        <p className="font-medium mb-4">{content.title}</p>
                        <Button asChild>
                            <a href={content.url} target="_blank" rel="noopener noreferrer">
                                <Eye className="h-4 w-4 mr-2" />
                                Ouvrir le document
                            </a>
                        </Button>
                    </div>
                </div>
            )}

            {content.type === 'article' && (
                <div className="prose prose-slate max-w-none">
                    <div 
                        className="p-6 bg-white rounded-lg border"
                        dangerouslySetInnerHTML={{ __html: content.content_text || '<p>Contenu non disponible</p>' }}
                    />
                </div>
            )}
        </div>
    );
};

// Composant Quiz - Question par question avec correction immédiate
const QuizComponent = ({ quiz, moduleId, onSubmit, previousScore, userQuizState }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [selectedOption, setSelectedOption] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [quizFinished, setQuizFinished] = useState(false);
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [navigating, setNavigating] = useState(false);
    const navigate = useNavigate();

    // Verrouillage des anciennes réponses : on n'affiche que les questions NON répondues
    const lockedIds = new Set(userQuizState?.locked_question_ids || []);
    const allQuestions = quiz?.questions || [];
    const pendingQuestions = allQuestions.filter(q => !lockedIds.has(q.id));
    const hasOldAnswers = lockedIds.size > 0;
    const isFullyPassedAndUpToDate = userQuizState?.is_passed && (userQuizState?.pending_count || 0) === 0;

    // Si quiz déjà 100% validé ET aucune nouvelle question en attente, montrer le résultat parfait
    if (isFullyPassedAndUpToDate && previousScore === 100 && !quizFinished && !result) {
        return (
            <div className="space-y-6" data-testid="quiz-results-already-perfect">
                <div className="text-center">
                    <Award className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <h2 className="text-xl font-bold">Résultats du Quiz</h2>
                </div>
                <Card className="border-emerald-400">
                    <CardContent className="py-8 text-center bg-gradient-to-b from-emerald-50 to-white rounded-lg">
                        <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
                        <h3 className="text-2xl font-bold text-emerald-700">Score parfait !</h3>
                        <p className="text-3xl font-bold text-emerald-600 mt-1">100%</p>
                        <p className="text-muted-foreground mt-2">Vous maîtrisez parfaitement ce module.</p>
                    </CardContent>
                </Card>
                <div className="flex flex-col items-center gap-3 pt-2">
                    <Button 
                        onClick={async () => {
                            setNavigating(true);
                            try {
                                const res = await academyAPI.getNextModule(moduleId);
                                if (res.data.has_next) {
                                    navigate(`/academy/module/${res.data.next_module_id}`);
                                } else {
                                    toast.info(res.data.message || "L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                                }
                            } catch {
                                toast.info("L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                            } finally {
                                setNavigating(false);
                            }
                        }}
                        size="lg" 
                        disabled={navigating}
                        data-testid="quiz-continue-btn"
                    >
                        {navigating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Continuer vers le module suivant
                        <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
        return (
            <Card>
                <CardContent className="py-8 text-center">
                    <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p>Aucun quiz disponible pour ce module</p>
                </CardContent>
            </Card>
        );
    }

    // Cas particulier : toutes les questions sont verrouillées (l'apporteur a déjà tout répondu)
    // mais le score reste < 100. On affiche son état actuel + bouton de navigation.
    if (pendingQuestions.length === 0 && !quizFinished && !result) {
        const score = userQuizState?.current_score ?? previousScore ?? 0;
        const passed = userQuizState?.is_passed || false;
        return (
            <div className="space-y-6" data-testid="quiz-already-completed">
                <div className="text-center">
                    <Award className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <h2 className="text-xl font-bold">Quiz terminé</h2>
                </div>
                <Card className={passed ? "border-emerald-400" : "border-amber-400"}>
                    <CardContent className={cn(
                        "py-8 text-center rounded-lg",
                        passed ? "bg-gradient-to-b from-emerald-50 to-white" : "bg-gradient-to-b from-amber-50 to-white"
                    )}>
                        {passed ? <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-4" /> : <Award className="h-16 w-16 mx-auto text-amber-500 mb-4" />}
                        <h3 className={cn("text-2xl font-bold", passed ? "text-emerald-700" : "text-amber-700")}>
                            {passed ? "Quiz validé !" : "Quiz en attente de nouvelles questions"}
                        </h3>
                        <p className={cn("text-3xl font-bold mt-1", passed ? "text-emerald-600" : "text-amber-600")}>{score}%</p>
                        <p className="text-muted-foreground mt-2">
                            Vous avez répondu à toutes les questions actuellement disponibles.
                        </p>
                    </CardContent>
                </Card>
                <div className="flex justify-center pt-2">
                    <Button onClick={() => navigate('/academy')} variant="outline" data-testid="quiz-back-to-academy">
                        Retour à l'Académie
                    </Button>
                </div>
            </div>
        );
    }

    const questions = pendingQuestions;
    const currentQuestion = questions[currentIndex];
    const totalQuestions = questions.length;
    const isCorrect = isAnswered && selectedOption === currentQuestion.correct_answer;
    const progressPercent = ((currentIndex + (isAnswered ? 1 : 0)) / totalQuestions) * 100;

    const handleSelectAnswer = (optionIndex) => {
        if (isAnswered) return; // Pas de changement après réponse
        setSelectedOption(optionIndex);
    };

    const handleConfirmAnswer = () => {
        if (selectedOption === null || isAnswered) return;
        setIsAnswered(true);
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: selectedOption }));
    };

    const handleNextQuestion = async () => {
        if (currentIndex < totalQuestions - 1) {
            setCurrentIndex(prev => prev + 1);
            setSelectedOption(null);
            setIsAnswered(false);
        } else {
            // Dernière question → soumettre au backend
            setSubmitting(true);
            try {
                const allAnswers = { ...answers, [currentQuestion.id]: selectedOption };
                const res = await academyAPI.submitQuiz(moduleId, allAnswers);
                setResult(res.data);
                setQuizFinished(true);
                onSubmit(res.data);
            } catch (err) {
                toast.error('Erreur lors de la soumission du quiz');
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleRetry = () => {
        setCurrentIndex(0);
        setAnswers({});
        setSelectedOption(null);
        setIsAnswered(false);
        setQuizFinished(false);
        setResult(null);
    };

    // ===================== ÉCRAN DE RÉSULTATS =====================
    if (quizFinished && result) {
        const isPerfect = result.score === 100;
        const isPassed = result.passed;
        const wrongQuestions = result.results?.filter(r => !r.is_correct) || [];

        return (
            <div className="space-y-6" data-testid="quiz-results">
                {/* Header */}
                <div className="text-center">
                    <Award className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <h2 className="text-xl font-semibold">Résultats du Quiz</h2>
                    <p className="text-muted-foreground text-sm">
                        {result.correct_count} / {result.total_questions} bonnes réponses
                    </p>
                </div>

                {/* Score Card */}
                <Card className={cn(
                    "overflow-hidden",
                    isPerfect && "border-emerald-400",
                    isPassed && !isPerfect && "border-amber-400",
                    !isPassed && "border-rose-400"
                )}>
                    <div className={cn(
                        "py-8 text-center",
                        isPerfect && "bg-gradient-to-b from-emerald-50 to-white",
                        isPassed && !isPerfect && "bg-gradient-to-b from-amber-50 to-white",
                        !isPassed && "bg-gradient-to-b from-rose-50 to-white"
                    )}>
                        {isPerfect ? (
                            <>
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-emerald-700">
                                    Score parfait !
                                </h3>
                                <p className="text-emerald-600 mt-1 text-lg font-semibold">{result.score}%</p>
                                <p className="text-emerald-600/80 mt-2">
                                    Vous maîtrisez parfaitement ce module.
                                </p>
                            </>
                        ) : isPassed ? (
                            <>
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                                    <AlertCircle className="h-10 w-10 text-amber-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-amber-700">
                                    Quiz réussi avec {result.score}%
                                </h3>
                                <p className="text-amber-600 mt-2">
                                    Attention, vous avez eu faux aux questions suivantes :
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
                                    <X className="h-10 w-10 text-rose-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-rose-700">
                                    Ce n'est pas encore acquis
                                </h3>
                                <p className="text-rose-600 mt-1 text-lg font-semibold">{result.score}% (minimum requis : {result.passing_score}%)</p>
                                <p className="text-rose-600/80 mt-2" data-testid="quiz-fail-message">
                                    Un minimum de 70 % de bonnes réponses est requis pour valider ce bloc (compréhension minimale). Vous pouvez reprendre le quiz autant de fois que nécessaire — repassez-le pour valider.
                                </p>
                            </>
                        )}
                    </div>
                </Card>

                {/* Détail des erreurs */}
                {wrongQuestions.length > 0 && (
                    <div className="space-y-3">
                        <h4 className="font-semibold text-slate-800">
                            Vos erreurs et explications :
                        </h4>
                        {wrongQuestions.map((wq, idx) => {
                            const q = questions.find(qq => qq.id === wq.question_id);
                            if (!q) return null;
                            return (
                                <Card key={wq.question_id} className="border-rose-200 bg-rose-50/30">
                                    <CardContent className="py-4 space-y-2">
                                        <p className="font-medium text-slate-900 flex items-start gap-2">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 text-rose-600 text-xs flex items-center justify-center mt-0.5">
                                                <X className="h-3.5 w-3.5" />
                                            </span>
                                            {q.question}
                                        </p>
                                        <div className="ml-8 space-y-1 text-sm">
                                            <p className="text-rose-600">
                                                Votre réponse : <span className="font-medium">{q.options[wq.user_answer] || '—'}</span>
                                            </p>
                                            <p className="text-emerald-600">
                                                Bonne réponse : <span className="font-medium">{q.options[wq.correct_answer]}</span>
                                            </p>
                                            {wq.explanation && (
                                                <div className="mt-2 p-2.5 bg-blue-50 rounded-lg text-blue-800 border border-blue-100">
                                                    <strong>Explication :</strong> {wq.explanation}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col items-center gap-3 pt-2">
                    {isPassed ? (
                        <>
                            <Button 
                                onClick={async () => {
                                    setNavigating(true);
                                    try {
                                        const res = await academyAPI.getNextModule(moduleId);
                                        if (res.data.has_next) {
                                            navigate(`/academy/module/${res.data.next_module_id}`);
                                        } else {
                                            toast.info(res.data.message || "L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                                        }
                                    } catch {
                                        toast.info("L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                                    } finally {
                                        setNavigating(false);
                                    }
                                }}
                                size="lg" 
                                disabled={navigating}
                                data-testid="quiz-continue-btn"
                            >
                                {navigating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                                Continuer vers le module suivant
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                            {!isPerfect && (
                                <Button onClick={handleRetry} variant="outline" size="lg" data-testid="quiz-retry-btn">
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Ou refaire le test pour atteindre 100%
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button onClick={handleRetry} size="lg" className="bg-rose-600 hover:bg-rose-700" data-testid="quiz-must-retry-btn">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refaire le test (obligatoire)
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // ===================== QUESTION EN COURS =====================
    return (
        <div className="space-y-6" data-testid="quiz-in-progress">
            {/* Banner : nouvelles questions ajoutées (l'apporteur a déjà répondu à des questions précédentes) */}
            {hasOldAnswers && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 flex items-start gap-3" data-testid="quiz-updates-banner">
                    <Sparkles className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-semibold">Nouvelles questions à répondre</p>
                        <p className="text-orange-800 mt-0.5">
                            {lockedIds.size} ancienne{lockedIds.size > 1 ? 's' : ''} réponse{lockedIds.size > 1 ? 's' : ''} verrouillée{lockedIds.size > 1 ? 's' : ''} · {pendingQuestions.length} nouvelle{pendingQuestions.length > 1 ? 's' : ''} question{pendingQuestions.length > 1 ? 's' : ''} à compléter pour mettre à jour votre score.
                        </p>
                    </div>
                </div>
            )}

            {/* Header + Progress */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-xl font-semibold flex items-center gap-2">
                            <Award className="h-5 w-5 text-yellow-500" />
                            Quiz de validation
                        </h2>
                        <p className="text-muted-foreground text-sm">
                            Score minimum requis : {quiz.passing_score}%
                        </p>
                    </div>
                    <Badge variant="outline" className="text-sm px-3 py-1">
                        Question {currentIndex + 1} / {totalQuestions}
                    </Badge>
                </div>
                <Progress value={progressPercent} className="h-2" />
            </div>

            {/* Question Card */}
            <Card className={cn(
                "transition-all duration-300",
                isAnswered && isCorrect && "border-emerald-400 shadow-emerald-100 shadow-lg",
                isAnswered && !isCorrect && "border-rose-400 shadow-rose-100 shadow-lg"
            )}>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-start gap-3">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center">
                            {currentIndex + 1}
                        </span>
                        <span className="pt-1">{currentQuestion.question}</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    {/* Options */}
                    {currentQuestion.options.map((option, oIndex) => {
                        const isSelected = selectedOption === oIndex;
                        const isCorrectOption = isAnswered && oIndex === currentQuestion.correct_answer;
                        const isWrongSelection = isAnswered && isSelected && oIndex !== currentQuestion.correct_answer;

                        return (
                            <button
                                key={oIndex}
                                onClick={() => handleSelectAnswer(oIndex)}
                                disabled={isAnswered}
                                className={cn(
                                    "w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-200",
                                    // Avant réponse
                                    !isAnswered && !isSelected && "border-slate-200 hover:border-primary/50 hover:bg-primary/5 cursor-pointer",
                                    !isAnswered && isSelected && "border-primary bg-primary/5 ring-2 ring-primary/20",
                                    // Après réponse
                                    isCorrectOption && "border-emerald-500 bg-emerald-50",
                                    isWrongSelection && "border-rose-500 bg-rose-50",
                                    isAnswered && !isCorrectOption && !isWrongSelection && "border-slate-100 opacity-50",
                                    isAnswered && "cursor-default"
                                )}
                                data-testid={`quiz-option-${oIndex}`}
                            >
                                <span className={cn(
                                    "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors",
                                    !isAnswered && !isSelected && "border-slate-300 text-slate-500",
                                    !isAnswered && isSelected && "border-primary bg-primary text-white",
                                    isCorrectOption && "border-emerald-500 bg-emerald-500 text-white",
                                    isWrongSelection && "border-rose-500 bg-rose-500 text-white"
                                )}>
                                    {isCorrectOption ? <Check className="h-4 w-4" /> :
                                     isWrongSelection ? <X className="h-4 w-4" /> :
                                     String.fromCharCode(65 + oIndex)}
                                </span>
                                <span className={cn(
                                    "flex-1 font-medium",
                                    isCorrectOption && "text-emerald-700",
                                    isWrongSelection && "text-rose-700"
                                )}>
                                    {option}
                                </span>
                            </button>
                        );
                    })}

                    {/* Correction + Explication */}
                    {isAnswered && (
                        <div className={cn(
                            "mt-4 p-4 rounded-xl border transition-all duration-300",
                            isCorrect 
                                ? "bg-emerald-50 border-emerald-200" 
                                : "bg-rose-50 border-rose-200"
                        )}>
                            <div className="flex items-start gap-3">
                                {isCorrect ? (
                                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                ) : (
                                    <X className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                                )}
                                <div>
                                    <p className={cn("font-semibold", isCorrect ? "text-emerald-700" : "text-rose-700")}>
                                        {isCorrect ? "Bonne réponse !" : "Mauvaise réponse"}
                                    </p>
                                    {!isCorrect && (
                                        <p className="text-sm text-rose-600 mt-1">
                                            La bonne réponse est : <strong>{currentQuestion.options[currentQuestion.correct_answer]}</strong>
                                        </p>
                                    )}
                                    {currentQuestion.explanation && (
                                        <p className="text-sm mt-2 text-slate-700">
                                            <strong>Explication :</strong> {currentQuestion.explanation}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Bouton d'action */}
            <div className="flex justify-center">
                {!isAnswered ? (
                    <Button
                        onClick={handleConfirmAnswer}
                        disabled={selectedOption === null}
                        size="lg"
                        className="min-w-[200px]"
                        data-testid="quiz-confirm-btn"
                    >
                        Valider ma réponse
                    </Button>
                ) : (
                    <Button
                        onClick={handleNextQuestion}
                        size="lg"
                        disabled={submitting}
                        className={cn(
                            "min-w-[200px]",
                            currentIndex === totalQuestions - 1 && "bg-yellow-500 hover:bg-yellow-600"
                        )}
                        data-testid="quiz-next-btn"
                    >
                        {submitting ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : null}
                        {currentIndex < totalQuestions - 1 ? (
                            <>
                                Question suivante
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </>
                        ) : (
                            <>Voir mes résultats</>
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default function ModuleDetail() {
    const { moduleId } = useParams();
    const navigate = useNavigate();
    const { user, isAdminFull } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [module, setModule] = useState(null);
    const [activeContent, setActiveContent] = useState(null);
    const [showQuiz, setShowQuiz] = useState(false);

    useEffect(() => {
        loadModule();
    }, [moduleId]);

    const loadModule = async () => {
        setLoading(true);
        try {
            const res = await academyAPI.getModule(moduleId);
            setModule(res.data);
            // Sélectionner le premier contenu UNIQUEMENT au premier chargement
            setActiveContent(prev => {
                if (!prev && res.data.contents?.length > 0) {
                    return res.data.contents[0];
                }
                return prev;
            });
        } catch (err) {
            console.error('Erreur chargement module:', err);
            const status = err.response?.status;
            if (status === 403) {
                toast.error('Ce module n\'est pas encore publié');
            } else {
                toast.error('Module non trouvé');
            }
            navigate('/academy');
        } finally {
            setLoading(false);
        }
    };

    const handleContentProgress = async (contentId, timeSpent, completed) => {
        try {
            await academyAPI.updateProgress(moduleId, {
                content_id: contentId,
                time_spent_seconds: timeSpent,
                completed
            });
            // Ne PAS recharger le module à chaque sauvegarde automatique
            // pour éviter de détruire/recréer la vidéo en cours de lecture.
            // On rafraîchit uniquement si l'utilisateur marque manuellement comme vu.
            if (completed) {
                const res = await academyAPI.getModule(moduleId);
                setModule(res.data);
                // Ne PAS changer activeContent pour ne pas perdre la vidéo en cours
            }
        } catch (err) {
            console.error('Erreur mise à jour progression:', err);
        }
    };

    const handleQuizSubmit = async (result) => {
        // Rafraîchir la progression SANS afficher le skeleton de chargement
        // pour ne pas détruire le QuizComponent et perdre l'écran de résultats
        try {
            const res = await academyAPI.getModule(moduleId);
            setModule(res.data);
        } catch (err) {
            console.error('Erreur rafraîchissement module:', err);
        }
    };

    const isContentViewed = (contentId) => {
        return module?.user_progress?.contents_progress?.[contentId]?.viewed;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-4">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid gap-4 lg:grid-cols-4">
                        <Skeleton className="h-96" />
                        <Skeleton className="h-96 lg:col-span-3" />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (!module) {
        return (
            <DashboardLayout>
                <div className="text-center py-12">
                    <p>Module non trouvé</p>
                    <Button onClick={() => navigate('/academy')} className="mt-4">
                        Retour à l'Académie
                    </Button>
                </div>
            </DashboardLayout>
        );
    }

    const progress = module.user_progress || {};
    const allContentsViewed = module.contents?.every(c => isContentViewed(c.id));

    return (
        <DashboardLayout>
            <div className="space-y-4" data-testid="module-detail-page">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/academy')}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="text-2xl font-bold">{module.title}</h1>
                        <p className="text-muted-foreground">{module.description}</p>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-muted-foreground mb-1">Progression</div>
                        <div className="flex items-center gap-2">
                            <Progress value={progress.progress_percent || 0} className="w-32 h-2" />
                            <span className="font-medium">{progress.progress_percent || 0}%</span>
                        </div>
                    </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-4">
                    {/* Sidebar - Liste des contenus */}
                    <Card className="lg:col-span-1">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Contenus du module</CardTitle>
                        </CardHeader>
                        <CardContent className="p-2">
                            <ScrollArea className="h-[500px]">
                                <div className="space-y-1">
                                    {module.contents?.map((content, index) => {
                                        const ContentIcon = CONTENT_ICONS[content.type] || BookOpen;
                                        const isViewed = isContentViewed(content.id);
                                        const isActive = activeContent?.id === content.id && !showQuiz;
                                        
                                        return (
                                            <button
                                                key={content.id}
                                                onClick={() => {
                                                    setActiveContent(content);
                                                    setShowQuiz(false);
                                                }}
                                                className={cn(
                                                    "w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors",
                                                    isActive ? "bg-primary text-primary-foreground" : "hover:bg-slate-100",
                                                    isViewed && !isActive && "text-emerald-600"
                                                )}
                                            >
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs">
                                                    {isViewed ? (
                                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </span>
                                                <ContentIcon className="h-4 w-4 flex-shrink-0" />
                                                <span className="truncate">{content.title}</span>
                                            </button>
                                        );
                                    })}
                                    
                                    {/* Quiz */}
                                    {module.has_quiz && (
                                        <>
                                            <Separator className="my-2" />
                                            <button
                                                onClick={() => setShowQuiz(true)}
                                                disabled={!allContentsViewed}
                                                className={cn(
                                                    "w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors",
                                                    showQuiz ? "bg-yellow-500 text-white" : "hover:bg-yellow-50",
                                                    !allContentsViewed && "opacity-50 cursor-not-allowed",
                                                    module.user_quiz_state?.is_passed && "text-emerald-600"
                                                )}
                                                data-testid="quiz-sidebar-btn"
                                            >
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center">
                                                    {module.user_quiz_state?.is_passed ? (
                                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                    ) : (
                                                        <Award className="h-4 w-4 text-yellow-600" />
                                                    )}
                                                </span>
                                                <span>Quiz de validation</span>
                                                {module.user_quiz_state?.has_updates && (
                                                    <Badge variant="outline" className="ml-auto text-[10px] py-0 h-5 border-orange-300 text-orange-700 bg-orange-50">
                                                        Mise à jour
                                                    </Badge>
                                                )}
                                                {!module.user_quiz_state?.has_updates && module.user_quiz_state?.current_score !== null && module.user_quiz_state?.current_score !== undefined && (
                                                    <Badge variant="secondary" className="ml-auto">
                                                        {module.user_quiz_state.current_score}%
                                                    </Badge>
                                                )}
                                            </button>
                                            {!allContentsViewed && (
                                                <p className="text-xs text-muted-foreground px-2">
                                                    Terminez tous les contenus pour accéder au quiz
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>

                    {/* Zone principale - Contenu ou Quiz */}
                    <Card className="lg:col-span-3">
                        <CardContent className="p-6">
                            {showQuiz ? (
                                <QuizComponent
                                    quiz={module.quiz}
                                    moduleId={moduleId}
                                    onSubmit={handleQuizSubmit}
                                    previousScore={progress.quiz_score}
                                    userQuizState={module.user_quiz_state}
                                />
                            ) : (
                                <ContentViewer
                                    content={activeContent}
                                    onComplete={handleContentProgress}
                                />
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Trames & supports du module — bien visible */}
                <ModuleResources moduleId={moduleId} canManage={isAdminFull()} />
            </div>
        </DashboardLayout>
    );
}
